import { Avatar, Message } from '@chatscope/chat-ui-kit-react';
import { ButtonUnstyled } from '@lifeomic/chroma-react/components/ButtonUnstyled';
import { ExpansionPanel } from '@lifeomic/chroma-react/components/ExpansionPanel';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Code, Copy, MessageThumbsUp } from '@lifeomic/chromicons';
import {
  footnoteDefinitionRegexp,
  footnoteRegexp,
} from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/ExecutiveSummary/utils';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isPlainObject from 'lodash/isPlainObject';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PostDetailsFragment } from '../__generated__/api.graphql';
import {
  CLINICIANS_AGENT_USERNAME,
  MEMBERS_AGENT_USERNAME,
} from '../constants';
import AgentIcon from '../images/ai-bot.svg';
import { CitationClass } from '../types';
import { getCitationClass } from './utils/getCitationClass';

// credit to https://stackoverflow.com/questions/23934656/how-can-i-copy-rich-text-contents-to-the-clipboard-with-javascript
function copyRichTextToClipboard(str: string) {
  function listener(e: ClipboardEvent) {
    e.clipboardData?.setData('text/html', str);
    e.clipboardData?.setData('text/plain', str);
    e.preventDefault();
  }
  document.addEventListener('copy', listener);
  document.execCommand('copy');
  document.removeEventListener('copy', listener);
}

function copyMarkdownToClipboard(str: string) {
  navigator.clipboard.writeText(str);
}

const useStyles = makeStyles((theme: Theme) => ({
  messageActionsPosition: {
    position: 'absolute',
    right: theme.spacing(9),
    width: theme.spacing(3),
    height: theme.spacing(3),
    paddingTop: theme.spacing(1),
  },
  messageActionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    margin: theme.spacing(1),
  },
  messageAction: {
    padding: 0,
    margin: 0,
  },
  cite: {
    color: theme.palette.primary.main,
    fontSize: theme.pxToRem(13),
    lineHeight: theme.pxToRem(8),
    '&:hover, &:focus': {
      textDecoration: 'underline',
    },
  },
}));

const messages = defineMessages({
  deletedMessage: {
    id: 'agentChat.chat.deletedMessage',
    defaultMessage: '<DELETED>',
  },
  leaveFeedback: {
    id: 'agentChat.chat.leaveFeedback',
    defaultMessage: 'Leave feedback on this response',
  },
  copyMessage: {
    id: 'agentChat.chat.copyMessage',
    defaultMessage: 'Copy message to clipboard',
  },
  copyMarkdown: {
    id: 'agentChat.chat.copyMarkdown',
    defaultMessage: 'Copy message markdown to clipboard',
  },
  copySuccess: {
    id: 'agentChat.chat.copySuccess',
    defaultMessage: 'Message copied to clipboard',
  },
  unknown: {
    id: 'agentChat.chat.unknown',
    defaultMessage: 'Unknown',
  },
  unknownAuthor: {
    id: 'agentChat.chat.unknownAuthor',
    defaultMessage: 'Author Unknown',
  },
  unknownMessage: {
    id: 'agentChat.chat.unknownMessage',
    defaultMessage: 'Message Unknown',
  },
});

const parseFootnoteRef = (
  href: string | undefined,
  ariaDescribedBy: string | undefined,
): [isFootnote: boolean, refId: string | undefined] => {
  // limit the parsing to footnotes; other links can exist in agent posts
  if (ariaDescribedBy === 'footnote-label') {
    // grab the reference id; expected format is '#user-content-fn-o56'
    const hrefParts = href?.split('-');
    const refId = hrefParts ? hrefParts[hrefParts.length - 1] : undefined;
    return [true, refId];
  } else {
    return [false, undefined];
  }
};

export type ChatMessageProps = {
  post: PostDetailsFragment;
  nextPost?: PostDetailsFragment;
  onMouseOver: () => void;
  isHovered: boolean;
  onClickFeedback: () => void;
  onClickCitation: (reference: string) => void;
  currentUserId: string;
};

const stripFootnotesFromMessage = (message: string) =>
  message
    .replace(footnoteDefinitionRegexp, '')
    .replace(footnoteRegexp, '')
    .trim();

export const ChatMessage: React.FC<ChatMessageProps> = ({
  post,
  nextPost,
  onMouseOver,
  isHovered,
  onClickFeedback,
  onClickCitation,
  currentUserId,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const displayTime = new Date(post.createdAt).toLocaleString([], {
    dateStyle: 'short',
    timeStyle: 'short',
  });
  const [notify] = useNotify();

  let maybePost =
    post.__typename == 'ActivePost'
      ? post?.message
      : formatMessage(messages.deletedMessage);
  const cleanedPost = useMemo(
    () => (maybePost ? stripFootnotesFromMessage(maybePost) : undefined),
    [maybePost],
  );

  const div = useRef(document.createElement('div'));
  useEffect(() => {
    ReactDOM.render(
      [
        <ReactMarkdown remarkPlugins={[remarkGfm]} key="rendered-post">
          {cleanedPost}
        </ReactMarkdown>,
      ] as any, // I could not get the types to work here, but it functions
      div.current,
    );
  }, [cleanedPost]);
  const renderedPost = div.current.innerHTML;

  const handleCopy = () => {
    copyRichTextToClipboard(renderedPost);
    notify(formatMessage(messages.copySuccess), 'info', 2000);
  };
  const handleCopyMarkdown = () => {
    copyMarkdownToClipboard(cleanedPost || '');
    notify(formatMessage(messages.copySuccess), 'info', 2000);
  };
  const isPostCopyable = !!renderedPost && !!cleanedPost;
  if (!maybePost) maybePost = formatMessage(messages.unknownMessage);

  let maybeAuthor =
    post.__typename == 'ActivePost'
      ? post?.author?.profile.displayName
      : formatMessage(messages.unknownAuthor);
  if (!maybeAuthor) maybeAuthor = formatMessage(messages.unknownAuthor);

  const authorUserId =
    post.__typename == 'ActivePost'
      ? post?.author?.userId
      : formatMessage(messages.unknown);

  const metadataString =
    post.__typename == 'ActivePost' ? post.metadata : undefined;
  let metadata: Record<string, unknown> = {};
  if (metadataString) {
    try {
      const parsed = JSON.parse(metadataString);
      if (_isPlainObject(parsed)) {
        metadata = parsed;
      }
    } catch {
      metadata = {};
    }
  }
  const isAgentMessage = [
    CLINICIANS_AGENT_USERNAME,
    MEMBERS_AGENT_USERNAME,
  ].includes(authorUserId || '');

  const metadataType =
    typeof metadata.type === 'string' ? metadata.type : undefined;

  const messageContent = (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a({ children, 'aria-describedby': ariaDescribedBy, ...props }) {
          const [isFootnote, refId] = parseFootnoteRef(
            props.href,
            ariaDescribedBy,
          );
          if (isFootnote) {
            // look up footnote contents
            const reference =
              refId && !_isEmpty(metadata) ? _get(metadata, refId) : undefined;

            const citationClass: CitationClass =
              typeof reference === 'string' ? getCitationClass(reference) : {};
            // if it is a supported type
            if (citationClass.citationType) {
              return (
                <ButtonUnstyled
                  title={citationClass.title}
                  onClick={() => {
                    if (isFootnote && typeof reference === 'string') {
                      onClickCitation(reference);
                    }
                  }}
                  className={classes.cite}
                >
                  [{children}]
                </ButtonUnstyled>
              );
            }
            return null; // return nothing for unsupported citation types
          }
          return <a {...props}>{children}</a>;
        },
        section({ children, ...props }) {
          if (props.className === 'footnotes') {
            return null;
          }
          return <section {...props}>{children}</section>;
        },
      }}
    >
      {maybePost}
    </ReactMarkdown>
  );

  return (
    <Message
      id={post.id}
      avatarPosition="top-left"
      onMouseOver={onMouseOver}
      model={{
        message: maybePost,
        sentTime: post.createdAt,
        sender: maybeAuthor,
        direction:
          authorUserId == null || authorUserId == currentUserId
            ? 'outgoing'
            : 'incoming',
        position: 'single',
      }}
    >
      {isAgentMessage && isHovered && (
        <Message.Header
          sender={maybeAuthor}
          className={classes.messageActionsPosition}
        >
          <div className={classes.messageActionsContainer}>
            {/* TODO: Allow feedback for member agent messages */}
            {authorUserId == CLINICIANS_AGENT_USERNAME && (
              <IconButton
                aria-label={formatMessage(messages.leaveFeedback)}
                icon={MessageThumbsUp}
                onClick={onClickFeedback}
                className={classes.messageAction}
              />
            )}
            {isPostCopyable && (
              <IconButton
                title={formatMessage(messages.copyMessage)}
                aria-label={formatMessage(messages.copyMessage)}
                icon={Copy}
                onClick={handleCopy}
                className={classes.messageAction}
              />
            )}
            {isPostCopyable && (
              <IconButton
                title={formatMessage(messages.copyMarkdown)}
                aria-label={formatMessage(messages.copyMarkdown)}
                icon={Code}
                onClick={handleCopyMarkdown}
                className={classes.messageAction}
              />
            )}
          </div>
        </Message.Header>
      )}

      <Message.CustomContent>
        {metadataType === 'llm-thinking' ? (
          <LLMThinkingMessage nextPost={nextPost}>
            {messageContent}
          </LLMThinkingMessage>
        ) : (
          messageContent
        )}
      </Message.CustomContent>
      {isAgentMessage && <Avatar name={maybeAuthor} src={AgentIcon} />}
      <Message.Footer sentTime={displayTime}></Message.Footer>
    </Message>
  );
};

const LLMThinkingMessage = ({
  children,
  nextPost,
}: {
  children: React.ReactNode;
  nextPost?: PostDetailsFragment;
}) => {
  const [isOpen, setIsOpen] = useState(nextPost ? false : true);

  useEffect(() => {
    if (nextPost) {
      setTimeout(() => setIsOpen(false), 3000);
    }
  }, [nextPost]);

  return (
    <ExpansionPanel
      isOpen={isOpen}
      title="Thoughts"
      style={{ borderBottom: 'none' }}
    >
      {children}
    </ExpansionPanel>
  );
};
